// Contratos do Portal do Condomínio — API `/api/v1/tenant`.
//
// O backend destas rotas será implementado pelo Codex (Feature 965). Todos os
// contratos assumidos pelo frontend ficam centralizados neste arquivo; ajustes
// de integração devem acontecer aqui e na implementação HTTP (http.ts) sem
// tocar nas telas. A implementação mock (src/mocks) usa exatamente estes tipos.

export type TenantRole =
  | "tenant_owner"
  | "tenant_admin"
  | "building_manager"
  | "concierge_operator"
  | "auditor";

export type SessionUser = {
  id: string;
  name: string;
  email: string;
};

export type TenantMembership = {
  tenant_id: string;
  tenant_name: string;
  role: TenantRole;
};

// GET /api/v1/tenant/session
export type SessionResponse = {
  user: SessionUser;
  memberships: TenantMembership[];
  active_tenant_id: string;
};

export type TenantContact = {
  name: string;
  role: string;
  email: string;
  phone: string;
};

// GET/PATCH /api/v1/tenant/profile
export type TenantProfile = {
  tenant_id: string;
  name: string;
  document: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  timezone: string;
  contacts: TenantContact[];
};

export type TenantProfilePatch = Partial<Omit<TenantProfile, "tenant_id">>;

export type AdminUserStatus = "active" | "invited" | "revoked";

// GET/POST/PATCH/DELETE /api/v1/tenant/admin-users
export type AdminUser = {
  id: string;
  name: string;
  email: string;
  role: TenantRole;
  status: AdminUserStatus;
  created_at: string;
  // Retornados somente ao criar um convite; nunca aparecem na listagem.
  activation_url?: string;
  pin?: string;
};

export type AdminUserInput = {
  name: string;
  email: string;
  role: TenantRole;
};

export type AdminUserPatch = Partial<Pick<AdminUser, "name" | "role">>;

// GET/POST/PATCH /api/v1/tenant/blocks
export type Block = {
  id: string;
  name: string;
  code: string | null;
  active: boolean;
};

export type BlockInput = { name: string; code?: string | null };
export type BlockPatch = Partial<BlockInput> & { active?: boolean };

// GET/POST/PATCH /api/v1/tenant/units
export type Unit = {
  id: string;
  block_id: string;
  block_name: string;
  label: string;
  floor: string | null;
  active: boolean;
};

export type UnitInput = { block_id: string; label: string; floor?: string | null };
export type UnitPatch = Partial<Omit<UnitInput, "block_id">> & { active?: boolean };

export type PersonStatus = "active" | "inactive";

// GET/POST/PATCH /api/v1/tenant/people
export type Person = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  status: PersonStatus;
};

export type PersonInput = { name: string; email?: string | null; phone?: string | null };
export type PersonPatch = Partial<PersonInput> & { status?: PersonStatus };

export type OccupancyType =
  | "owner"
  | "resident"
  | "lessee"
  | "family"
  | "staff"
  | "guest";

// GET/POST/PATCH/DELETE /api/v1/tenant/occupancies
export type Occupancy = {
  id: string;
  person_id: string;
  person_name: string;
  unit_id: string;
  unit_label: string;
  block_name: string;
  type: OccupancyType;
  valid_from: string;
  valid_until: string | null;
  call_priority: number;
  can_receive_calls: boolean;
  can_activate_app: boolean;
  is_primary: boolean;
  is_temporary: boolean;
  active: boolean;
};

export type OccupancyInput = {
  person_id: string;
  unit_id: string;
  type: OccupancyType;
  valid_from: string;
  valid_until?: string | null;
  call_priority?: number;
  can_receive_calls?: boolean;
  can_activate_app?: boolean;
  is_primary?: boolean;
  is_temporary?: boolean;
};

export type OccupancyPatch = Partial<Omit<OccupancyInput, "person_id" | "unit_id">> & {
  active?: boolean;
};

export type InvitationStatus = "pending" | "used" | "expired" | "revoked";

// POST /api/v1/tenant/occupancies/{id}/invitations
// POST /api/v1/tenant/invitations/{id}/revoke | /resend
// GET  /api/v1/tenant/invitations  (contrato adicional assumido — necessário
//      para a tela de convites; confirmar com o Codex na integração)
export type Invitation = {
  id: string;
  occupancy_id: string;
  person_name: string;
  unit_label: string;
  status: InvitationStatus;
  // Link universal de ativação; o QR Code é gerado no cliente a partir dele.
  // Nunca registrar este valor em logs.
  // Retornados somente na criação ou no reenvio. O backend guarda apenas hash.
  activation_url?: string;
  pin?: string;
  expires_at: string;
  created_at: string;
};

export type DevicePlatform = "ios" | "android";

// GET/DELETE /api/v1/tenant/devices
export type Device = {
  id: string;
  person_id: string;
  person_name: string;
  unit_labels: string[];
  platform: DevicePlatform;
  name: string;
  last_seen_at: string | null;
  active: boolean;
};

export type ImportRowError = {
  row: number;
  field: string;
  message: string;
};

// POST /api/v1/tenant/imports/preview (multipart: file)
export type ImportPreview = {
  import_id: string;
  file_name: string;
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  errors: ImportRowError[];
};

// POST /api/v1/tenant/imports/commit ({ import_id })
export type ImportCommitResult = {
  import_id: string;
  status: "committed";
  created: Record<string, number>;
};

// GET /api/v1/tenant/audit-events
export type AuditEvent = {
  id: string;
  actor_name: string;
  actor_role: TenantRole | "platform";
  action: string;
  entity_type: string;
  entity_label: string | null;
  created_at: string;
};

export type AuditFilters = {
  action?: string;
  entity_type?: string;
};

// Interface única consumida pelas telas. `createHttpApi` (real) e
// `createMockApi` (desenvolvimento) implementam este contrato.
// Toda chamada scoped recebe o tenant ativo e o envia em `X-AVA-Tenant-ID`;
// o backend revalida o vínculo do usuário — o header nunca concede acesso.
export interface PortalApi {
  getSession(): Promise<SessionResponse>;
  redeemOwnerInvitation(inviteToken: string, pin: string): Promise<{ tenant_id: string; role: TenantRole }>;

  getProfile(tenantId: string): Promise<TenantProfile>;
  updateProfile(tenantId: string, patch: TenantProfilePatch): Promise<TenantProfile>;

  listAdminUsers(tenantId: string): Promise<AdminUser[]>;
  inviteAdminUser(tenantId: string, input: AdminUserInput): Promise<AdminUser>;
  updateAdminUser(tenantId: string, id: string, patch: AdminUserPatch): Promise<AdminUser>;
  removeAdminUser(tenantId: string, id: string): Promise<void>;

  listBlocks(tenantId: string): Promise<Block[]>;
  createBlock(tenantId: string, input: BlockInput): Promise<Block>;
  updateBlock(tenantId: string, id: string, patch: BlockPatch): Promise<Block>;

  listUnits(tenantId: string): Promise<Unit[]>;
  createUnit(tenantId: string, input: UnitInput): Promise<Unit>;
  updateUnit(tenantId: string, id: string, patch: UnitPatch): Promise<Unit>;

  listPeople(tenantId: string): Promise<Person[]>;
  createPerson(tenantId: string, input: PersonInput): Promise<Person>;
  updatePerson(tenantId: string, id: string, patch: PersonPatch): Promise<Person>;

  listOccupancies(tenantId: string): Promise<Occupancy[]>;
  createOccupancy(tenantId: string, input: OccupancyInput): Promise<Occupancy>;
  updateOccupancy(tenantId: string, id: string, patch: OccupancyPatch): Promise<Occupancy>;
  deleteOccupancy(tenantId: string, id: string): Promise<void>;

  listInvitations(tenantId: string): Promise<Invitation[]>;
  createInvitation(tenantId: string, occupancyId: string): Promise<Invitation>;
  resendInvitation(tenantId: string, id: string): Promise<Invitation>;
  revokeInvitation(tenantId: string, id: string): Promise<Invitation>;

  listDevices(tenantId: string): Promise<Device[]>;
  revokeDevice(tenantId: string, id: string): Promise<void>;

  previewImport(tenantId: string, file: File): Promise<ImportPreview>;
  commitImport(tenantId: string, importId: string): Promise<ImportCommitResult>;

  listAuditEvents(tenantId: string, filters?: AuditFilters): Promise<AuditEvent[]>;
}

export const OCCUPANCY_TYPE_LABELS: Record<OccupancyType, string> = {
  owner: "Proprietário",
  resident: "Morador",
  lessee: "Locatário",
  family: "Familiar",
  staff: "Funcionário",
  guest: "Hóspede",
};

export const ROLE_LABELS: Record<TenantRole, string> = {
  tenant_owner: "Responsável pelo condomínio",
  tenant_admin: "Administrador",
  building_manager: "Gestor predial",
  concierge_operator: "Operador de portaria",
  auditor: "Auditor",
};

export const INVITATION_STATUS_LABELS: Record<InvitationStatus, string> = {
  pending: "Pendente",
  used: "Utilizado",
  expired: "Expirado",
  revoked: "Revogado",
};

export type OccupancyValidity = "active" | "expired" | "future" | "inactive";

// Situação temporal do vínculo, derivada no cliente para exibição.
export function occupancyValidity(occupancy: Occupancy, now: Date = new Date()): OccupancyValidity {
  if (!occupancy.active) return "inactive";
  if (new Date(occupancy.valid_from) > now) return "future";
  if (occupancy.valid_until && new Date(occupancy.valid_until) < now) return "expired";
  return "active";
}

export const OCCUPANCY_VALIDITY_LABELS: Record<OccupancyValidity, string> = {
  active: "Vigente",
  expired: "Expirado",
  future: "Futuro",
  inactive: "Inativo",
};
